import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRealtime } from '../../contexts/RealtimeContext';
import { getUserRole, ROLES } from '../../utils/rbac';

const RoleBasedRedirect = ({ children }) => {
  const { currentUser, userProfile, isInitialized } = useRealtime();
  const navigate = useNavigate();
  const location = useLocation();
  const lastRedirectPath = useRef(null);

  useEffect(() => {
    // Only redirect if user is authenticated and we have their profile
    if (!isInitialized || !currentUser) {
      return;
    }

    // CRITICAL: Wait for userProfile to load before making redirect decisions
    // This prevents defaulting to student dashboard while profile is loading
    if (!userProfile) {
      console.log('⏳ Waiting for user profile to load...');
      return;
    }

    const currentPath = location.pathname;
    
    // Prevent redirect loop - if we just redirected to this path, don't redirect again
    if (lastRedirectPath.current === currentPath) {
      console.log('⏸️ Skipping redirect - already at target:', currentPath);
      return;
    }
    
    // Skip redirect for certain paths
    const skipRedirectPaths = [
      '/login',
      '/register',
      '/forgot-password',
      '/reset-password',
      '/logout',
      '/contact',
      '/terms',
      '/privacy',
      '/score-calculator',
      '/badge/',
      '/hobbies',
      '/join-activity/',
      '/coaching-signup',
      '/admissions',
      '/institute/',
      '/blog'
    ];

    if (skipRedirectPaths.some(path => currentPath.startsWith(path))) {
      console.log('⏸️ Skipping redirect - on excluded path:', currentPath);
      return;
    }

    // Get user role
    const userRole = getUserRole(userProfile, currentUser);
    
    console.log('🔄 RoleBasedRedirect Check:', { 
      currentPath, 
      userRole, 
      role: userProfile.role, 
      userType: userProfile.userType,
      coachingId: userProfile.coachingId,
      uid: currentUser.uid,
      email: currentUser.email
    });

    // Determine target path based on role
    let targetPath = null;

    switch (userRole) {
      case ROLES.TEACHER:
        // Teachers should be on /teacher/* routes
        if (!currentPath.startsWith('/teacher/') && !currentPath.startsWith('/coaching/')) {
          // Redirect from ANY non-teacher path (except excluded ones)
          targetPath = '/teacher/dashboard';
          console.log('👨‍🏫 Teacher detected, should redirect to:', targetPath);
        } else {
          console.log('✅ Teacher already on correct path:', currentPath);
        }
        break;

      case ROLES.COACHING_ADMIN:
      case 'coaching_staff':
        if (!currentPath.startsWith('/coaching-admin') && !currentPath.startsWith('/coaching/')) {
          if (currentPath === '/' || currentPath === '/dashboard' || currentPath.startsWith('/dashboard/')) {
            targetPath = '/coaching-admin';
            console.log('🏫 Coaching Admin/Staff detected, should redirect to:', targetPath);
          }
        }
        break;

      case ROLES.ADMIN:
      case ROLES.SUPERADMIN:
        if (!currentPath.startsWith('/admin') && !currentPath.startsWith('/founder')) {
          if (currentPath === '/' || currentPath === '/dashboard' || currentPath.startsWith('/dashboard/')) {
            targetPath = '/admin';
          }
        }
        break;

      case ROLES.FOUNDER:
        if (!currentPath.startsWith('/founder') && !currentPath.startsWith('/admin')) {
          if (currentPath === '/' || currentPath === '/dashboard' || currentPath.startsWith('/dashboard/')) {
            targetPath = '/founder';
          }
        }
        break;

      case ROLES.STUDENT:
      default:
        // Students stay on regular dashboard, only redirect from home
        if (currentPath === '/') {
          targetPath = '/dashboard';
        }
        console.log('👨‍🎓 Student detected, current path:', currentPath);
        break;
    }

    // Perform redirect if needed
    if (targetPath && targetPath !== currentPath) {
      console.log('➡️ REDIRECTING FROM:', currentPath, 'TO:', targetPath);
      lastRedirectPath.current = targetPath;
      
      // Use setTimeout to ensure state updates complete
      setTimeout(() => {
        navigate(targetPath, { replace: true });
      }, 0);
    } else if (targetPath) {
      console.log('✅ Already at target path:', currentPath);
    }
  }, [currentUser, userProfile, isInitialized, navigate, location.pathname]);

  // If user is logged in but profile is not loaded, show loading
  // This prevents children (which may have their own protected route logic) 
  // from running prematurely and potentially redirecting incorrectly
  if (currentUser && !userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return children;
};

export default RoleBasedRedirect;